import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { FaCalendarAlt } from "react-icons/fa";
import dayjs from "dayjs";
import styles from "./AdmissionSchedule.module.css";
import {
  CustomButton,
  CustomDatePicker,
  CustomTextInput,
  CustomDropdown,
  CustomTimePicker,
} from "../../../../components";
import AxiosInstance, { TokenService } from "../../../../services/Api";
import PopupModal from "../../../../components/PopupModal/PopupModal";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";

const ADMISSION_TYPE_OPTIONS = [
  { value: 1, label: "VI-X" },
  { value: 2, label: "XI-XII" },
];

const AdmissionScheduleCreate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const editId = location.state?.ID || null;

  const { translations } = useSelector((state) => state.language);
  const t = translations?.labels;

  const user = TokenService.getUser();
  const clientData = user?.client_data[0] || {};
  const client_id = clientData?.client_id || "";
  const school_id = clientData?.school_id || "";
  const user_id = user?.id || "";
  const academic_year = clientData?.academic_year || "";
  const academic_year_id = clientData?.academic_year_id || "";

  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    admissionType: "",
    date: null,
    name: "",
    venue: "",
    fromTime: null,
    toTime: null,
  });
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [searchInput, setSearchInput] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [errors, setErrors] = useState({});
  const [modalState, setModalState] = useState({
    open: false,
    type: "success",
    message: "",
    redirectPath: "",
  });
  const [selectedApplicationIds, setSelectedApplicationIds] = useState([]);
  // Store original IDs from edit mode to keep them always selected
  const [originalEditIds, setOriginalEditIds] = useState([]);

  // ─────────────────────────────────────────────
  //  Fetch edit data
  // ─────────────────────────────────────────────
  useEffect(() => {
    if (editId) {
      fetchInterviewScheduleView();
    }
  }, [editId]);

  const fetchInterviewScheduleView = async () => {
    setLoading(true);
    try {
      const response = await AxiosInstance.get(
        `/admission/interviewSchedule/${editId}`,
      );

      if (response?.data?.status) {
        const list = response.data.data.schedule_list || [];
        const formatted = list.map((item, index) => ({
          sno: index + 1,
          ...item,
        }));
        setData(formatted);
        setRowCount(formatted.length);

        // Pre-select all existing students and lock them
        const existingIds = list.map((item) => String(item.application_id));
        setSelectedApplicationIds(existingIds);
        setOriginalEditIds(existingIds);

        const scheduleData = response.data.data.schedule;
        setFormData({
          admissionType: scheduleData.admission_type || "",
          date: scheduleData.date ? dayjs(scheduleData.date) : null,
          fromTime: scheduleData.start_time
            ? dayjs(scheduleData.start_time, "HH:mm:ss")
            : null,
          toTime: scheduleData.end_time
            ? dayjs(scheduleData.end_time, "HH:mm:ss")
            : null,
          name: scheduleData.name || "",
          venue: scheduleData.interview_venue || "",
        });
      }
    } catch (error) {
      console.error("Error fetching interview schedule details:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.fetchFailed || "Failed to fetch details. Please try again.",
        redirectPath: "",
      });
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  //  Fetch seat allocated list
  // ─────────────────────────────────────────────
  const fetchSeatAllocatedList = useCallback(
    async (admissionType, search = "") => {
      setLoading(true);
      try {
        const payload = {
          admission_type:
            typeof admissionType === "object"
              ? admissionType.label
              : admissionType,
          school_id: school_id,
          search: search,
          academic_year_id: Number(academic_year_id),
        };

        const response = await AxiosInstance.post(
          "/admission/getSeatAllocatedList",
          payload,
        );

        if (response?.data?.status) {
          const list = response.data.data || [];
          const formatted = list.map((item, index) => ({
            sno: index + 1,
            ...item,
          }));
          setData(formatted);
          setRowCount(response.data.pagination?.total || formatted.length);
        } else {
          setData([]);
          setRowCount(0);
        }
      } catch (error) {
        console.error("Seat allocation fetch error:", error);
        setData([]);
        setRowCount(0);
      } finally {
        setLoading(false);
      }
    },
    [school_id],
  );

  // ─────────────────────────────────────────────
  //  Form handlers
  // ─────────────────────────────────────────────
  const clearError = (name) => {
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);

    if (name === "admissionType" && value) {
      setSelectedApplicationIds([]);
      fetchSeatAllocatedList(value, "");
    }
  };

  const handleDateChange = (name, date) => {
    setFormData((prev) => ({ ...prev, [name]: date }));
    clearError(name);
  };

  const handleTimeChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    clearError(name);
  };

  // ─────────────────────────────────────────────
  //  Validation
  // ─────────────────────────────────────────────
  const validateForm = () => {
    const newErrors = {};

    if (!formData.admissionType) {
      newErrors.admissionType =
        t?.admissionTypeRequired || "Admission Type is required";
    }

    if (!formData.name || formData.name.trim() === "") {
      newErrors.name = t?.nameRequired || "Name is required";
    }

    if (!formData.date) {
      newErrors.date = t?.dateRequired || "Date is required";
    } else {
      const today = dayjs().startOf("day");
      if (dayjs(formData.date).isBefore(today)) {
        newErrors.date =
          t?.dateMustBeTodayOrFuture || "Date must be today or a future date";
      }
    }

    if (!formData.fromTime) {
      newErrors.fromTime = t?.fromTimeRequired || "From Time is required";
    }

    if (!formData.toTime) {
      newErrors.toTime = t?.toTimeRequired || "To Time is required";
    }

    // To Time must be after From Time
    if (formData.fromTime && formData.toTime) {
      if (formData.toTime.isBefore(formData.fromTime)) {
        newErrors.toTime =
          t?.toTimeMustBeAfterFromTime || "To Time must be after From Time";
      }
    }

    if (!formData.venue || formData.venue.trim() === "") {
      newErrors.venue = t?.venueRequired || "Venue is required";
    }

    // At least one student must be selected
    if (selectedApplicationIds.length === 0) {
      setModalState({
        open: true,
        type: "error",
        message:
          t?.selectAtLeastOneStudent ||
          "Please select at least one student from the Seat Allocated Application List",
      });
      setErrors(newErrors);
      return false;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ─────────────────────────────────────────────
  //  Submit
  // ─────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      let payload;
      let response;

      if (editId) {
        payload = {
          id: editId,
          client_id: client_id,
          school_id: school_id,
          date: formData.date
            ? dayjs(formData.date).format("YYYY-MM-DD")
            : null,
          start_time: formData.fromTime
            ? formData.fromTime.format("HH:mm")
            : "",
          end_time: formData.toTime ? formData.toTime.format("HH:mm") : "",
          venue: formData.venue,
          application_id: selectedApplicationIds,
          updated_by: user_id,
        };

        response = await AxiosInstance.put(
          "/admission/interviewSchedule/update",
          payload,
        );
      } else {
        payload = {
          client_id: client_id,
          academic_year: academic_year_id,
          admission_type:
            typeof formData.admissionType === "object"
              ? formData.admissionType.label
              : formData.admissionType,
          school_id: school_id,
          name: formData.name,
          date: formData.date
            ? dayjs(formData.date).format("YYYY-MM-DD")
            : null,
          start_time: formData.fromTime
            ? formData.fromTime.format("HH:mm")
            : "",
          end_time: formData.toTime ? formData.toTime.format("HH:mm") : "",
          venue: formData.venue,
          application_id: selectedApplicationIds,
          created_by: user_id,
        };

        response = await AxiosInstance.post(
          "/admission/interviewSchedule/create",
          payload,
        );
      }

      if (response?.data?.status) {
        setModalState({
          open: true,
          type: "success",
          message:
            response.data?.message ||
            (editId
              ? t?.interviewUpdateSuccess ||
                "Interview schedule updated successfully!"
              : t?.interviewCreateSuccess ||
                "Interview schedule created successfully!"),
          redirectPath: "/user/admission-schedule",
        });
      } else {
        setModalState({
          open: true,
          type: "error",
          message:
            response.data?.message ||
            t?.somethingWentWrong ||
            "Something went wrong!",
          redirectPath: "",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setModalState({
        open: true,
        type: "error",
        message: t?.interviewSaveFailed || "Failed to save interview schedule.",
        redirectPath: "",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate("/user/admission-schedule");
  };

  const handleSuccessOk = () => {
    const { type, redirectPath } = modalState;
    setModalState((prev) => ({ ...prev, open: false }));
    if (type === "success" && redirectPath) {
      navigate(redirectPath);
    }
  };

  // ─────────────────────────────────────────────
  //  Checkbox handlers
  // ─────────────────────────────────────────────
  const paginatedData = useMemo(() => {
    const start = pagination.pageIndex * pagination.pageSize;
    const end = start + pagination.pageSize;
    return data.slice(start, end);
  }, [data, pagination.pageIndex, pagination.pageSize]);

  const isAllPageSelected = useMemo(() => {
    if (paginatedData.length === 0) return false;
    return paginatedData.every((row) =>
      selectedApplicationIds.includes(String(row.application_id)),
    );
  }, [paginatedData, selectedApplicationIds]);

  const handleSelectAll = useCallback(
    (e) => {
      if (e.target.checked) {
        const pageIds = paginatedData.map((item) =>
          String(item.application_id),
        );
        setSelectedApplicationIds((prev) => [
          ...new Set([...prev, ...pageIds]),
        ]);
      } else {
        // In edit mode, don't allow deselecting original IDs
        const pageIds = paginatedData.map((item) =>
          String(item.application_id),
        );
        setSelectedApplicationIds((prev) =>
          prev.filter(
            (id) => !pageIds.includes(id) || originalEditIds.includes(id),
          ),
        );
      }
    },
    [paginatedData, originalEditIds],
  );

  const handleSelectRow = useCallback(
    (applicationId) => {
      const id = String(applicationId);

      // In edit mode, don't allow deselecting original students
      if (editId && originalEditIds.includes(id)) return;

      setSelectedApplicationIds((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
      );
    },
    [editId, originalEditIds],
  );

  // ─────────────────────────────────────────────
  //  Columns
  // ─────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        id: "select",
        header: () => (
          <input
            type="checkbox"
            checked={isAllPageSelected}
            onChange={handleSelectAll}
            className={styles.radioInput}
          />
        ),
        size: 50,
        Cell: ({ row }) => {
          const id = String(row.original.application_id);
          const isOriginal = editId && originalEditIds.includes(id);

          return (
            <input
              type="checkbox"
              checked={selectedApplicationIds.includes(id)}
              onChange={() => handleSelectRow(row.original.application_id)}
              disabled={isOriginal}
              className={styles.radioInput}
              style={isOriginal ? { opacity: 0.6, cursor: "not-allowed" } : {}}
            />
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "sno",
        header: t?.serialNo || "S.No.",
        size: 70,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "application_no",
        header: t?.applicationId || "Application ID",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "student_name",
        header: t?.name || "Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "assigned_class",
        header: t?.classLabel || "Class",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "assigned_section",
        header: t?.section || "Section",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "mobile_number",
        header: t?.mobileNumber || "Mobile Number",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "date",
        header: t?.appliedDate || "Applied Date",
        enableSorting: true,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const value = cell.getValue();
          return value ? dayjs(value).format("DD-MM-YYYY") : "-";
        },
      },
    ],
    [
      t,
      selectedApplicationIds,
      isAllPageSelected,
      handleSelectAll,
      handleSelectRow,
      editId,
      originalEditIds,
    ],
  );

  // ─────────────────────────────────────────────
  //  Loading state
  // ─────────────────────────────────────────────
  if (loading && !data.length) {
    return (
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {t?.interviewSchedule || "Interview Schedule"}
          </h1>
        </div>
        <div className={styles.loadingContainer}>
          <p>{t?.loading || "Loading..."}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <PopupModal
        open={modalState.open}
        title={
          modalState.type === "success"
            ? t?.success || "Success"
            : t?.failed || "Failed"
        }
        message={modalState.message}
        onOk={handleSuccessOk}
      />

      <div className={styles.container}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerIcon}>
            <FaCalendarAlt />
          </div>
          <h1 className={styles.headerTitle}>
            {t?.interviewSchedule || "Interview Schedule"}
          </h1>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="row">
            {/* Academic Year */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.academicYear || "Academic Year"}
                name="academicYear"
                type="text"
                value={academic_year}
                disabled
                readOnly
              />
            </div>

            {/* Admission Type */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomDropdown
                label={t?.admissionType || "Admission Type"}
                name="admissionType"
                value={formData.admissionType}
                onChange={(value) => handleSelectChange("admissionType", value)}
                options={ADMISSION_TYPE_OPTIONS}
                placeholder={t?.selectAdmissionType || "Select admission type"}
                required
                disabled={!!editId}
                error={errors.admissionType}
              />
            </div>

            {/* Name */}
            <div className="col-lg-4 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.name || "Name"}
                name="name"
                type="text"
                value={formData.name}
                onChange={handleInputChange}
                disabled={!!editId}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.name}
              />
            </div>

            {/* Date */}
            <div className="col-lg-2 px-2 col-md-6 col-sm-12">
              <CustomDatePicker
                label={t?.date || "Date"}
                name="date"
                value={formData.date}
                onChange={(date) => handleDateChange("date", date)}
                placeholder="Ex: 30/07/2016"
                required
                error={errors.date}
                minDate={dayjs()}
              />
            </div>

            {/* From Time */}
            <div className="col-lg-2 px-2 col-md-6 col-sm-12">
              <CustomTimePicker
                label={t?.fromTime || "From Time"}
                name="fromTime"
                value={formData.fromTime}
                onChange={(value) => handleTimeChange("fromTime", value)}
                placeholder="Ex: 09:30"
                required
                error={errors.fromTime}
              />
            </div>

            {/* To Time */}
            <div className="col-lg-2 px-2 col-md-6 col-sm-12">
              <CustomTimePicker
                label={t?.toTime || "To Time"}
                name="toTime"
                value={formData.toTime}
                onChange={(value) => handleTimeChange("toTime", value)}
                placeholder="Ex: 12:00"
                required
                error={errors.toTime}
              />
            </div>

            {/* Venue */}
            <div className="col-lg-6 px-2 col-md-6 col-sm-12">
              <CustomTextInput
                label={t?.venue || "Venue"}
                name="venue"
                type="text"
                value={formData.venue}
                onChange={handleInputChange}
                placeholder={t?.enterHere || "Enter here.."}
                required
                error={errors.venue}
              />
            </div>
          </div>

          {/* Seat Allocated List Header */}
          <div className={styles.header}>
            <div className={styles.headerIcon}>
              <FaCalendarAlt />
            </div>
            <h1 className={styles.headerTitle}>
              {t?.seatAllocationApplication ||
                "Seat Allocated Application List"}
            </h1>
          </div>

          {/* Table */}
          <CustomMRTTable
            enableRowSelection={false}
            columns={columns}
            data={paginatedData}
            loading={loading}
            pagination={pagination}
            rowCount={rowCount}
            onPaginationChange={setPagination}
            searchInput={searchInput}
            placeholder={t?.searchName || "Search Name"}
            setSearchInput={setSearchInput}
            onSearchEnter={(value) => {
              fetchSeatAllocatedList(formData.admissionType, value);
              setPagination((prev) => ({ ...prev, pageIndex: 0 }));
            }}
            enableColumnFilters={false}
          />

          {/* Buttons */}
          <div className={styles.buttonContainer}>
            <CustomButton
              type="submit"
              variant="contained"
              className={styles.submitButton}
              fullWidth={false}
              loading={submitting}
              disabled={submitting}
              loadingText={
                editId
                  ? t?.updating || "Updating..."
                  : t?.submitting || "Submitting..."
              }
            >
              {editId ? t?.update || "UPDATE" : t?.save || "SAVE"}
            </CustomButton>

            <CustomButton
              type="button"
              variant="contained"
              className={styles.cancelButton}
              fullWidth={false}
              onClick={handleCancel}
              disabled={submitting}
            >
              {t?.cancel || "CANCEL"}
            </CustomButton>
          </div>
        </form>
      </div>
    </>
  );
};

export default AdmissionScheduleCreate;
